// CodingForm.jsx
import React, { useState } from 'react';
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import axios from 'axios';
import { API_BASE_URL } from './api';
import { Button } from 'react-bootstrap';

export default function CodingForm({
  categories,
  orderedCategories,
  currentResponse,
  coderName,
  setShowWarning,
  updateResponse,
  handleNoteChange,
  projectSlug,
  videoId,
  project,
  onSaveProject,
  onSubmitSuccess
}) {
  const isExcluded = currentResponse.excluded || false;
  const isSubmitted = currentResponse.status === "submitted";

  // Same semantic as backend: at least one category with a non-empty tag list (excluding notes, excluded, status)
  const categoriesOnlyForValidation = Object.fromEntries(
    Object.entries(currentResponse || {}).filter(([key]) => key !== 'notes' && key !== 'excluded' && key !== 'status')
  );
  const hasAtLeastOneTag = Object.values(categoriesOnlyForValidation).some(
    (arr) => Array.isArray(arr) && arr.length > 0
  );

  const handleSubmit = async () => {
    if (!coderName.trim()) return setShowWarning(true);
    if (isSubmitted) {
      alert('This response has already been submitted.');
      return;
    }
    if (!isExcluded && !hasAtLeastOneTag) {
      alert('Select at least one tag or mark the video as excluded.');
      return;
    }

    try {
      let response;
      if (isExcluded) {
        // For excluded videos, only send the excluded status
        response = await axios.post(`${API_BASE_URL}/api/submit`, {
          project: projectSlug,
          coder: coderName,
          video_id: videoId,
          excluded: true,
          notes: currentResponse.notes || ""
        });
      } else {
        // Extract category responses (excluding notes and excluded status)
        const categoriesOnly = Object.fromEntries(
          Object.entries(currentResponse).filter(([key]) => key !== 'notes' && key !== 'excluded' && key !== 'status')
        );

        response = await axios.post(`${API_BASE_URL}/api/submit`, {
          project: projectSlug,
          coder: coderName,
          video_id: videoId,
          categories: categoriesOnly,
          notes: currentResponse.notes || ""
        });
      }
      // Update local state to mark as submitted - pass response so status can be extracted
      if (onSubmitSuccess) {
        onSubmitSuccess(response);
      }
    } catch (err) {
      console.error('Submit error:', err);
      const msg = err.response?.data?.error || 'Submission failed.';
      alert(msg);
    }
  };

  return (
    <div className="px-3 py-2 d-flex flex-column" style={{ width: '100%', fontSize: '0.85rem', lineHeight: '1.1rem' }}>

      {/* Show submitted status */}
      {isSubmitted && (
        <div className="alert alert-success mb-2" style={{ fontSize: '0.9em', padding: '0.5rem' }}>
          ✓ Submitted
        </div>
      )}

      {/* Category blocks - disabled when excluded or submitted */}
      <div style={{ pointerEvents: (isExcluded || isSubmitted) ? 'none' : 'auto' }}>
        {(orderedCategories && orderedCategories.length > 0
          ? orderedCategories
          : Object.keys(categories).map((name) => ({ category: name, tags: categories[name] || [] }))
        ).map((catItem, catIndex) => {
        const catName = catItem.category;
        const options = (catItem.tags || []).map((t) => typeof t === 'string' ? t : t?.tag).filter(Boolean);
        return (
          <div key={`category-${catIndex}`} className="mb-2">
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.9em' }}>{catName}</label>

            <div className="d-flex flex-wrap gap-1 mb-1">
              {options.length > 0 && options.map((tag, tagIndex) => {
                const isSelected = (currentResponse[catName] || []).some((t) => t === tag);
                const inputId = `cat-${catIndex}-tag-${tagIndex}`;

                return (
                  <div key={`tag-${catIndex}-${tagIndex}`}>
                    {!(isSubmitted || isExcluded) ? (
                      <>
                        <input
                          type="checkbox"
                          className="btn-check"
                          id={inputId}
                          autoComplete="off"
                          checked={isSelected}
                          onChange={() => {
                            if (!coderName.trim()) return setShowWarning(true);
                            const current = currentResponse[catName] || [];
                            const updated = isSelected
                              ? current.filter((t) => t !== tag)
                              : [...current, tag];
                            updateResponse(catName, updated);
                          }}
                        />
                        <label
                          className="btn btn-outline-primary btn-sm"
                          htmlFor={inputId}
                          style={{ 
                            whiteSpace: 'nowrap', 
                            minWidth: 'fit-content', 
                            paddingTop: '0.15rem', 
                            paddingBottom: '0.15rem', 
                            paddingLeft: '0.4rem', 
                            paddingRight: '0.4rem', 
                            fontSize: '0.95em'
                          }}
                        >
                          {tag}
                        </label>
                      </>
                    ) : (
                      <span
                        className={isSelected ? "btn btn-primary btn-sm" : "btn btn-outline-primary btn-sm"}
                        style={{ 
                          whiteSpace: 'nowrap', 
                          minWidth: 'fit-content', 
                          paddingTop: '0.15rem', 
                          paddingBottom: '0.15rem', 
                          paddingLeft: '0.4rem', 
                          paddingRight: '0.4rem', 
                          fontSize: '0.95em',
                          opacity: !isSelected ? 0.5 : 1,
                          cursor: 'not-allowed',
                          display: 'inline-block'
                        }}
                      >
                        {isSelected && '✓ '}
                        {tag}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>

      {/* Notes field */}
      <div className="mt-auto">
        <label className="form-label fw-semibold" style={{ fontSize: '0.9em' }}>Notes</label>
        <Textarea
          value={currentResponse.notes || ''}
          onChange={handleNoteChange}
          className="form-control"
          rows={3}
          style={{ fontSize: '0.9em' }}
          disabled={isSubmitted}
        />

        <div className="d-flex justify-content-between align-items-center mt-1" style={{ paddingBottom: '0.25rem' }}>
          <div className="form-check d-flex align-items-center">
            <input
              className="form-check-input"
              type="checkbox"
              id="exclude-video"
              checked={isExcluded}
              disabled={isSubmitted}
              onChange={(e) => {
                if (!coderName.trim()) return setShowWarning(true);
                const updated = {
                  ...currentResponse,
                  excluded: e.target.checked
                };
                // Clear all category selections when excluding
                if (e.target.checked) {
                  Object.keys(categories).forEach(cat => {
                    updated[cat] = [];
                  });
                }
                // Update the response
                Object.keys(updated).forEach(key => {
                  if (key === 'excluded') {
                    // Handle excluded status specially
                    if (e.target.checked) {
                      updateResponse('excluded', true);
                    } else {
                      updateResponse('excluded', false);
                    }
                  } else if (key !== 'notes') {
                    updateResponse(key, updated[key]);
                  }
                });
              }}
            />
            <label className="form-check-label fw-semibold p-2 d-flex align-items-end" htmlFor="exclude-video" style={{ fontSize: '0.9em', color: isExcluded ? '#dc3545' : 'inherit' }}>
              Exclude this video from dataset
            </label>
          </div>
          <Button
            variant="success"
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitted || (!isExcluded && !hasAtLeastOneTag)}
          >
            {isSubmitted ? 'Submitted' : 'Submit'}
          </Button>
        </div>
      </div>

    </div>
  );
}
